import { createHash } from 'node:crypto';
import {
  cpSync,
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  renameSync,
  rmSync,
  statSync,
  writeFileSync,
} from 'node:fs';
import { basename, join, relative, resolve } from 'node:path';
import { packageVersion } from '../../version.js';
import type { ParsedArgs } from '../args.js';
import { flag, flagAll } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { packageRoot } from '../package-files.js';
import { requireConfirmation } from './common.js';

const MANIFEST_NAME = '.zaparoo-cli-skills.json';
const CLIENT_TARGETS = {
  agents: '.agents/skills',
  claude: '.claude/skills',
  cursor: '.cursor/skills',
  copilot: '.github/skills',
} as const;

type AgentClient = keyof typeof CLIENT_TARGETS;

interface SkillRecord {
  name: string;
  hash: string;
}

interface SkillManifest {
  schemaVersion: 1;
  package: '@zaparoo/cli';
  version: string;
  client: AgentClient | 'custom';
  skills: SkillRecord[];
}

export async function agentCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'doctor';
  if (action === 'doctor') return doctor(args);
  if (action !== 'install' && action !== 'update') {
    throw new CliError(`Unknown agent action "${action}"`, ExitCode.Usage);
  }
  requireConfirmation(
    args,
    `${action === 'install' ? 'Installing' : 'Updating'} Agent Skills requires confirmation`,
  );
  return installOrUpdate(args, action);
}

function installOrUpdate(args: ParsedArgs, action: 'install' | 'update'): CommandResult {
  const sourceRoot = resolve(packageRoot(), 'skills');
  const available = availableSkills(sourceRoot);
  const selected = selectedSkills(args, available);
  const destination = targetDirectory(args);
  if (action === 'update' && !existsSync(destination)) {
    throw new CliError(
      `Agent skill target does not exist: ${destination}; run agent install first`,
      ExitCode.Usage,
    );
  }
  mkdirSync(destination, { recursive: true });

  const existingManifest = readManifest(destination);
  const records = new Map((existingManifest?.skills ?? []).map((entry) => [entry.name, entry]));
  for (const name of selected) {
    const source = resolve(sourceRoot, name);
    const target = resolve(destination, name);
    const sourceHash = directoryHash(source);
    if (action === 'install' && existsSync(target)) {
      const targetHash = directoryHash(target);
      if (targetHash !== sourceHash) {
        throw new CliError(
          `${name} already exists with different content; use agent update after review`,
          ExitCode.Usage,
        );
      }
    } else {
      rmSync(target, { recursive: true, force: true });
      cpSync(source, target, { recursive: true, force: true });
    }
    records.set(name, { name, hash: sourceHash });
  }
  const installed = [...records.values()].sort((a, b) => a.name.localeCompare(b.name));

  const manifest: SkillManifest = {
    schemaVersion: 1,
    package: '@zaparoo/cli',
    version: packageVersion,
    client: flag(args.flags, 'target') ? 'custom' : client(args),
    skills: installed,
  };
  writeManifest(destination, manifest);
  return {
    data: {
      success: true,
      action,
      target: destination,
      version: packageVersion,
      skills: selected,
      reloadRequired: true,
    },
    human: `${action === 'install' ? 'Installed' : 'Updated'} ${selected.length} skills in ${destination}; reload agent session`,
  };
}

function doctor(args: ParsedArgs): CommandResult {
  const sourceRoot = resolve(packageRoot(), 'skills');
  const available = availableSkills(sourceRoot);
  const selected = selectedSkills(args, available);
  const destination = targetDirectory(args);
  const manifest = readManifest(destination);
  const checks = selected.map((name) => {
    const source = resolve(sourceRoot, name);
    const target = resolve(destination, name);
    if (!existsSync(target)) return { name, ok: false, reason: 'missing' };
    const expectedHash = directoryHash(source);
    const actualHash = directoryHash(target);
    const manifestHash = manifest?.skills.find((entry) => entry.name === name)?.hash;
    const ok = expectedHash === actualHash && manifestHash === expectedHash;
    return {
      name,
      ok,
      reason:
        expectedHash !== actualHash
          ? 'content-mismatch'
          : manifestHash !== expectedHash
            ? 'manifest-mismatch'
            : undefined,
      expectedHash,
      actualHash,
      manifestHash,
    };
  });
  const ok = checks.every((check) => check.ok) && manifest?.version === packageVersion;
  const detectedClients = Object.entries(CLIENT_TARGETS)
    .filter(([, path]) => existsSync(resolve(process.cwd(), path)))
    .map(([name]) => name);
  return {
    data: {
      ok,
      cli: { package: '@zaparoo/cli', version: packageVersion, node: process.version },
      target: destination,
      manifest: manifest ?? null,
      checks,
      detectedClients,
      guidance: ok
        ? 'Reload the agent session after skill updates.'
        : 'Run agent install or agent update with --yes after reviewing the target.',
    },
    human: ok
      ? `Agent Skills OK in ${destination}`
      : `Agent Skills need attention in ${destination}`,
    exitCode: ok ? ExitCode.Success : ExitCode.General,
  };
}

function client(args: ParsedArgs): AgentClient {
  const value = flag(args.flags, 'client') ?? 'agents';
  if (value in CLIENT_TARGETS) return value as AgentClient;
  throw new CliError('--client must be agents, claude, cursor, or copilot', ExitCode.Usage);
}

function targetDirectory(args: ParsedArgs): string {
  const target = flag(args.flags, 'target');
  if (target !== undefined && !target.trim()) {
    throw new CliError('--target must not be empty', ExitCode.Usage);
  }
  return resolve(target ?? CLIENT_TARGETS[client(args)]);
}

function availableSkills(root: string): string[] {
  if (!existsSync(root))
    throw new CliError('Packaged Agent Skills are unavailable', ExitCode.General);
  return readdirSync(root, { withFileTypes: true })
    .filter((entry) => entry.isDirectory() && existsSync(resolve(root, entry.name, 'SKILL.md')))
    .map((entry) => entry.name)
    .sort();
}

function selectedSkills(args: ParsedArgs, available: string[]): string[] {
  const requested = flagAll(args.flags, 'skill');
  if (requested.length === 0) return available;
  const invalid = requested.filter((name) => !available.includes(name));
  if (invalid.length > 0) {
    throw new CliError(
      `Unknown packaged skill: ${invalid.join(', ')}; available: ${available.join(', ')}`,
      ExitCode.Usage,
    );
  }
  return [...new Set(requested)].sort();
}

function directoryHash(directory: string): string {
  const hash = createHash('sha256');
  const files = collectFiles(directory);
  for (const file of files) {
    hash.update(relative(directory, file).replaceAll('\\', '/'));
    hash.update('\0');
    hash.update(readFileSync(file));
    hash.update('\0');
  }
  return hash.digest('hex');
}

function collectFiles(directory: string): string[] {
  const files: string[] = [];
  for (const entry of readdirSync(directory, { withFileTypes: true })) {
    const path = join(directory, entry.name);
    if (entry.isDirectory()) files.push(...collectFiles(path));
    else if (entry.isFile()) files.push(path);
  }
  return files.sort();
}

function readManifest(directory: string): SkillManifest | undefined {
  const path = resolve(directory, MANIFEST_NAME);
  if (!existsSync(path) || !statSync(path).isFile()) return undefined;
  try {
    const value = JSON.parse(readFileSync(path, 'utf8')) as SkillManifest;
    return value.package === '@zaparoo/cli' && value.schemaVersion === 1 ? value : undefined;
  } catch {
    return undefined;
  }
}

function writeManifest(directory: string, manifest: SkillManifest): void {
  const path = resolve(directory, MANIFEST_NAME);
  const temporary = resolve(directory, `.${basename(MANIFEST_NAME)}.${process.pid}.tmp`);
  writeFileSync(temporary, `${JSON.stringify(manifest, null, 2)}\n`, { mode: 0o600 });
  renameSync(temporary, path);
}
